import {
  nextPhNumber,
  POTHOLE_GEO_ITEM_TYPE,
  phNumberKey,
  phNumberOf,
} from '@civilgrid/utils/src/geoItems/potholeNumberClash';
import { useMemo } from 'react';
import type { GeoItem } from '~/common/types';
import { useListGeoItems } from './common';

/** The PH# field's error when the typed number is already used on the project. */
export const PH_NUMBER_TAKEN = 'Number is already taken.';

export interface PhNumberClash {
  /** One more than the project's highest PH#, when any is numeric. */
  suggestion?: string;
}

/**
 * The clash for a PH# typed into a pothole's form, or null when there is none. Mirrors the
 * server rule: only a changed PH# is checked, against the project's other active potholes.
 */
export function findPhNumberClash(
  geoItems: readonly Pick<GeoItem, 'id' | 'type' | 'status' | 'props'>[],
  geoItemId: string,
  initialPhNumber: unknown,
  phNumber: unknown,
): PhNumberClash | null {
  const key = phNumberKey(phNumber);
  if (!key || key === phNumberKey(initialPhNumber)) return null;

  const others = geoItems
    .filter((g) => g.id !== geoItemId && g.type === POTHOLE_GEO_ITEM_TYPE && g.status === 'active')
    .map((g) => phNumberOf(g.props));
  if (!others.some((value) => phNumberKey(value) === key)) return null;

  return { suggestion: nextPhNumber(others) };
}

/** {@link findPhNumberClash} over the project's geo items already loaded for the potholing page. */
export function usePhNumberClash({
  projectId,
  geoItemId,
  initialPhNumber,
  phNumber,
}: {
  projectId: string;
  geoItemId: string;
  initialPhNumber: unknown;
  phNumber: unknown;
}) {
  const { data: geoItems } = useListGeoItems({ projectId });
  return useMemo(
    () => (geoItems ? findPhNumberClash(geoItems, geoItemId, initialPhNumber, phNumber) : null),
    [geoItems, geoItemId, initialPhNumber, phNumber],
  );
}
